// src/components/ReferralPage.jsx
// Referral program dashboard — KFX gold-on-dark aesthetic — fully mobile responsive

import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api.js';

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt$(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const STATUS_PILL = {
  PENDING:  { bg: 'rgba(245,200,60,.10)',  color: '#F5C842', label: 'Pending'  },
  ACTIVE:   { bg: 'rgba(60,200,100,.10)',  color: '#4CD97A', label: 'Active'   },
  PAID:     { bg: 'rgba(212,168,67,.12)',  color: '#D4A843', label: 'Paid'     },
  EXPIRED:  { bg: 'rgba(255,100,80,.10)', color: '#FF6B55', label: 'Expired'  },
  APPROVED: { bg: 'rgba(60,200,100,.10)', color: '#4CD97A', label: 'Approved' },
  REJECTED: { bg: 'rgba(255,100,80,.10)', color: '#FF6B55', label: 'Rejected' },
};

function Pill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.PENDING;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
      textTransform: 'uppercase', padding: '3px 9px', borderRadius: 20,
      fontFamily: 'monospace', whiteSpace: 'nowrap',
      display: 'inline-block',
    }}>{s.label}</span>
  );
}

function StatCard({ icon, label, value, sub, accent }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.025)',
      border: `1px solid ${accent ? 'rgba(212,168,67,0.3)' : 'rgba(255,255,255,0.07)'}`,
      borderRadius: 12,
      padding: '16px 18px',
      display: 'flex', flexDirection: 'column', gap: 4,
      minWidth: 0,
    }}>
      <div style={{ fontSize: 16 }}>{icon}</div>
      <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'monospace' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent ? '#D4A843' : '#E8E4DC', letterSpacing: '-0.02em', lineHeight: 1, wordBreak: 'break-word' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'rgba(232,228,220,0.3)', marginTop: 1, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

// ── Mobile referral card (replaces table row on small screens) ─────────────────
function ReferralCard({ r }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 10, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: '#E8E4DC', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.refereeName}</div>
          <div style={{ fontSize: 11, color: 'rgba(232,228,220,0.3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.refereeEmail}</div>
        </div>
        <Pill status={r.status} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.3)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Joined</div>
          <div style={{ fontSize: 12, color: 'rgba(232,228,220,0.55)' }}>{fmtDate(r.joinedAt)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.3)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Converted</div>
          <div style={{ fontSize: 12, color: 'rgba(232,228,220,0.55)' }}>{fmtDate(r.convertedAt)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.3)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Plan</div>
          <div style={{ fontSize: 12, color: r.planName ? '#E8E4DC' : 'rgba(232,228,220,0.25)' }}>
            {r.planName || '—'}
            {r.planAmount ? <span style={{ fontSize: 11, color: 'rgba(232,228,220,0.35)', marginLeft: 4 }}>{fmt$(r.planAmount)}</span> : null}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.3)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Commission</div>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace', color: r.commissionEarned > 0 ? '#D4A843' : 'rgba(232,228,220,0.25)' }}>
            {r.commissionEarned > 0 ? fmt$(r.commissionEarned) : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mobile withdrawal card ─────────────────────────────────────────────────────
function WithdrawalCard({ w }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 10, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontWeight: 700, color: '#D4A843', fontFamily: 'monospace', fontSize: 16 }}>{fmt$(w.amount)}</div>
        <Pill status={w.status} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.3)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Date</div>
          <div style={{ fontSize: 12, color: 'rgba(232,228,220,0.55)' }}>{fmtDate(w.requestedAt)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.3)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Method</div>
          <div style={{ fontSize: 12, color: 'rgba(232,228,220,0.6)' }}>{w.method || '—'}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.3)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Processed</div>
          <div style={{ fontSize: 12, color: 'rgba(232,228,220,0.55)' }}>{fmtDate(w.processedAt)}</div>
        </div>
        {w.note && (
          <div>
            <div style={{ fontSize: 10, color: 'rgba(232,228,220,0.3)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Note</div>
            <div style={{ fontSize: 12, color: 'rgba(232,228,220,0.35)' }}>{w.note}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────
function WithdrawModal({ stats, withdrawalMin, onClose, onSuccess }) {
  const [amount,  setAmount]  = useState('');
  const [method,  setMethod]  = useState('Mobile Money');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [done,    setDone]    = useState(false);

  const submit = async () => {
    setError('');
    if (!amount || Number(amount) <= 0) return setError('Enter a valid amount.');
    if (Number(amount) < withdrawalMin) return setError(`Minimum withdrawal is $${withdrawalMin}.`);
    if (Number(amount) > Number(stats.availableBalance)) return setError('Amount exceeds available balance.');
    if (!details.trim()) return setError('Enter your payment details.');
    setLoading(true);
    try {
      await api('/referral/withdraw', {
        method: 'POST',
        body: JSON.stringify({ amount: Number(amount), method, details }),
      });
      setDone(true);
      onSuccess();
    } catch (e) {
      setError(e.message || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, padding: '11px 14px', color: '#E8E4DC', fontSize: 14, outline: 'none',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: 'rgba(232,228,220,0.4)',
    textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'monospace', marginBottom: 6,
    display: 'block',
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: 0,
      }}
    >
      {/* Sheet slides up from bottom on mobile, centered on desktop */}
      <div style={{
        background: '#111318', border: '1px solid rgba(212,168,67,0.2)',
        borderRadius: '18px 18px 0 0',
        width: '100%', maxWidth: 480,
        overflow: 'hidden',
        maxHeight: '92dvh',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Gold top bar */}
        <div style={{ height: 3, background: 'linear-gradient(90deg,#c4a05a,#e0c080,#c4a05a)', flexShrink: 0 }} />
        {/* Drag handle hint */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0', flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.12)' }} />
        </div>

        <div style={{ padding: '16px 20px 28px', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#E8E4DC' }}>Withdraw Funds</h2>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(232,228,220,0.6)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '4px 10px', borderRadius: 8, fontWeight: 700 }}>×</button>
          </div>

          {/* Balance pill */}
          <div style={{
            background: 'rgba(212,168,67,0.06)', border: '1px solid rgba(212,168,67,0.18)',
            borderRadius: 10, padding: '12px 16px', marginBottom: 20,
          }}>
            <div style={{ fontSize: 10, color: 'rgba(212,168,67,0.55)', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Available Balance</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#D4A843', fontFamily: 'monospace' }}>{fmt$(stats.availableBalance)}</div>
          </div>

          {done ? (
            <div style={{
              textAlign: 'center', padding: '28px 20px',
              background: 'rgba(60,200,100,0.06)', border: '1px solid rgba(60,200,100,0.2)',
              borderRadius: 12,
            }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#4CD97A', marginBottom: 8 }}>Request Submitted!</div>
              <div style={{ fontSize: 13, color: 'rgba(232,228,220,0.5)', lineHeight: 1.6 }}>
                We'll process your withdrawal within 3 business days.
              </div>
              <button onClick={onClose} style={{
                marginTop: 20, padding: '12px 32px',
                background: 'rgba(60,200,100,0.15)', border: '1px solid rgba(60,200,100,0.3)',
                color: '#4CD97A', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                width: '100%',
              }}>Close</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Amount */}
              <div>
                <label style={labelStyle}>Amount (USD) — min ${withdrawalMin}</label>
                <input
                  type="number" min={withdrawalMin} step="0.01"
                  max={stats.availableBalance}
                  placeholder={`Max ${fmt$(stats.availableBalance)}`}
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  style={inputStyle}
                />
              </div>

              {/* Method */}
              <div>
                <label style={labelStyle}>Payment Method</label>
                <select
                  value={method}
                  onChange={e => setMethod(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'none' }}
                >
                  <option>Mobile Money</option>
                  <option>Bank Transfer</option>
                  <option>PayPal</option>
                  <option>Crypto (USDT)</option>
                  <option>Other</option>
                </select>
              </div>

              {/* Details */}
              <div>
                <label style={labelStyle}>Payment Details</label>
                <textarea
                  rows={3}
                  placeholder="Phone number, account number, wallet address, etc."
                  value={details}
                  onChange={e => setDetails(e.target.value)}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
                />
              </div>

              {error && (
                <div style={{
                  background: 'rgba(255,100,80,.08)', border: '1px solid rgba(255,100,80,.2)',
                  borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#FF6B55',
                }}>{error}</div>
              )}

              <button
                onClick={submit}
                disabled={loading}
                style={{
                  width: '100%', padding: '14px',
                  background: loading ? 'rgba(212,168,67,0.15)' : 'linear-gradient(135deg,#c4a05a,#a0803a)',
                  color: loading ? 'rgba(11,13,17,0.4)' : '#0b0d11',
                  fontWeight: 800, fontSize: 13, letterSpacing: '0.08em',
                  textTransform: 'uppercase', border: 'none', borderRadius: 10,
                  cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'monospace',
                  minHeight: 48,
                }}
              >
                {loading ? 'Submitting…' : 'Submit Withdrawal Request'}
              </button>

              <p style={{ margin: 0, fontSize: 11, color: 'rgba(232,228,220,0.25)', textAlign: 'center', lineHeight: 1.5 }}>
                Processed within 3 business days after review.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Responsive styles injected once ──────────────────────────────────────────
const RESPONSIVE_STYLES = `
  .ref-stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  .ref-withdraw-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;
    gap: 12px;
  }
  .ref-table-wrap { display: block; }
  .ref-cards-wrap  { display: none; }
  .ref-tabs-bar {
    display: flex;
    gap: 4px;
    background: rgba(255,255,255,0.03);
    border-radius: 9px;
    padding: 4px;
    width: fit-content;
    margin-bottom: 12px;
    flex-wrap: nowrap;
  }
  .ref-link-row {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(212,168,67,0.2);
    border-radius: 9px;
    padding: 11px 14px;
  }
  .ref-link-copy-btn {
    flex-shrink: 0;
  }
  .ref-how-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }
  @media (max-width: 680px) {
    .ref-stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .ref-how-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (max-width: 520px) {
    .ref-withdraw-bar {
      flex-direction: column;
      align-items: stretch;
    }
    .ref-withdraw-bar > div { text-align: center; }
    .ref-withdraw-btn { width: 100% !important; }
    .ref-table-wrap { display: none; }
    .ref-cards-wrap  { display: flex; flex-direction: column; gap: 10px; }
    .ref-tabs-bar {
      width: 100%;
    }
    .ref-tabs-bar button {
      flex: 1;
    }
    .ref-link-row {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
    }
    .ref-link-text {
      white-space: normal !important;
      word-break: break-all !important;
      text-overflow: unset !important;
      overflow: visible !important;
    }
    .ref-link-copy-btn {
      width: 100%;
      text-align: center;
    }
    .ref-stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .ref-how-grid {
      grid-template-columns: 1fr 1fr;
    }
  }
  @media (max-width: 360px) {
    .ref-stats-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  /* Modal sheet: center on tablet+, bottom-sheet on mobile */
  @media (min-width: 521px) {
    .ref-modal-backdrop {
      align-items: center !important;
      padding: 20px !important;
    }
    .ref-modal-sheet {
      border-radius: 18px !important;
      max-height: 90vh !important;
    }
    .ref-modal-handle { display: none !important; }
  }
`;

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ReferralPage({ onCountChange }) {
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [copied,       setCopied]       = useState(false);
  const [tab,          setTab]          = useState('referrals');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [refPage,      setRefPage]      = useState(1);
  const REF_PAGE_SIZE = 10;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await api('/referral/dashboard');
      setData(d);
      onCountChange?.(d?.stats?.totalReferrals ?? 0);
    } catch (e) {
      setError(e.message || 'Failed to load referral data.');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    if (!data?.link) return;
    navigator.clipboard.writeText(data.link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 340, color: '#E8E4DC' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 32, height: 32, border: '2px solid rgba(212,168,67,.2)', borderTopColor: '#D4A843', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 12px' }} />
        <div style={{ fontSize: 12, color: 'rgba(232,228,220,0.4)', fontFamily: 'monospace' }}>Loading referral data…</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
      <div style={{ color: '#FF6B55', fontSize: 14, marginBottom: 16 }}>{error}</div>
      <button onClick={load} style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.3)', color: '#D4A843', padding: '9px 22px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Try Again</button>
    </div>
  );

  if (!data) return null;

  const { stats, referrals: bellReferrals, allReferrals = bellReferrals, withdrawals, link, commissionRate, withdrawalMin } = data;
  const referrals = allReferrals;
  const canWithdraw = Number(stats.availableBalance) >= withdrawalMin;

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '4px 16px 80px', color: '#E8E4DC', fontFamily: "'Inter', sans-serif" }}>
      <style>{RESPONSIVE_STYLES}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.22)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
          }}>🤝</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>Referral Program</h1>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: 'rgba(232,228,220,0.4)', lineHeight: 1.65 }}>
          Earn <strong style={{ color: '#D4A843' }}>{(commissionRate * 100).toFixed(0)}%</strong> of every referred user's premium subscription.
          Withdraw once you reach <strong style={{ color: '#D4A843' }}>${withdrawalMin}</strong>.
        </p>
      </div>

      {/* ── Referral link ── */}
      <div style={{
        background: 'rgba(212,168,67,0.03)', border: '1px solid rgba(212,168,67,0.16)',
        borderRadius: 14, padding: '16px 18px', marginBottom: 20,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(212,168,67,0.55)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'monospace', marginBottom: 10 }}>
          Your Referral Link
        </div>
        <div className="ref-link-row">
          <span className="ref-link-text" style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, color: '#D4A843', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {link}
          </span>
          <button
            className="ref-link-copy-btn"
            onClick={copyLink}
            style={{
              background: copied ? 'rgba(60,200,100,0.15)' : 'rgba(212,168,67,0.14)',
              border: `1px solid ${copied ? 'rgba(60,200,100,0.3)' : 'rgba(212,168,67,0.3)'}`,
              color: copied ? '#4CD97A' : '#D4A843',
              padding: '9px 18px', borderRadius: 7, fontSize: 12, fontWeight: 700,
              letterSpacing: '0.06em', cursor: 'pointer', whiteSpace: 'nowrap',
              fontFamily: 'monospace', textTransform: 'uppercase', transition: 'all .15s',
              minHeight: 40,
            }}
          >
            {copied ? '✓ Copied!' : 'Copy Link'}
          </button>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: 'rgba(232,228,220,0.28)', lineHeight: 1.6 }}>
          Share this link. When your friend subscribes, you earn {(commissionRate * 100).toFixed(0)}% automatically.
        </p>
      </div>

      {/* ── Stats grid ── */}
      <div className="ref-stats-grid" style={{ marginBottom: 16 }}>
        <StatCard icon="👥" label="Total Referrals"  value={stats.totalReferrals}   sub={`${stats.pendingReferrals} pending`} />
        <StatCard icon="✅" label="Converted"         value={stats.activeReferrals}  sub="went premium" />
        <StatCard icon="💰" label="Total Earned"       value={fmt$(stats.totalEarned)}         accent />
        <StatCard icon="🏦" label="Available"          value={fmt$(stats.availableBalance)}     accent={canWithdraw} sub={canWithdraw ? 'Ready to withdraw' : `Need ${fmt$(withdrawalMin - Number(stats.availableBalance))} more`} />
        <StatCard icon="📤" label="Withdrawn"         value={fmt$(stats.totalWithdrawn)} />
      </div>

      {/* ── Withdraw CTA bar ── */}
      <div className="ref-withdraw-bar" style={{
        background: canWithdraw ? 'rgba(212,168,67,0.05)' : 'rgba(255,255,255,0.02)',
        border: `1px solid ${canWithdraw ? 'rgba(212,168,67,0.22)' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: 12, padding: '14px 18px', marginBottom: 28,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: canWithdraw ? '#D4A843' : 'rgba(232,228,220,0.45)' }}>
            {canWithdraw
              ? `${fmt$(stats.availableBalance)} ready to withdraw`
              : `Earn ${fmt$(withdrawalMin - Number(stats.availableBalance))} more to unlock withdrawals`}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(232,228,220,0.28)', marginTop: 3 }}>
            Payments processed within 3 business days
          </div>
        </div>
        <button
          className="ref-withdraw-btn"
          onClick={() => setShowWithdraw(true)}
          disabled={!canWithdraw}
          style={{
            padding: '10px 22px', borderRadius: 9, fontWeight: 700, fontSize: 12,
            letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: 'monospace',
            border: 'none', whiteSpace: 'nowrap', flexShrink: 0,
            background: canWithdraw ? 'linear-gradient(135deg,#c4a05a,#a0803a)' : 'rgba(255,255,255,0.05)',
            color: canWithdraw ? '#0b0d11' : 'rgba(232,228,220,0.2)',
            cursor: canWithdraw ? 'pointer' : 'not-allowed',
            minHeight: 42,
          }}
        >
          Withdraw Funds
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="ref-tabs-bar">
        {[['referrals', `👥 Referrals (${referrals.length})`], ['withdrawals', `📤 Withdrawals (${withdrawals.length})`]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              background: tab === key ? 'rgba(212,168,67,0.12)' : 'none',
              border: 'none', cursor: 'pointer',
              padding: '8px 16px', borderRadius: 7,
              fontSize: 12, fontWeight: 600,
              color: tab === key ? '#D4A843' : 'rgba(232,228,220,0.38)',
              fontFamily: 'monospace', letterSpacing: '0.03em',
              transition: 'all .15s', whiteSpace: 'nowrap',
              minHeight: 36,
            }}
          >{label}</button>
        ))}
      </div>

      {/* ── Referrals ── */}
      {tab === 'referrals' && (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'hidden' }}>
          {referrals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '52px 24px', color: 'rgba(232,228,220,0.3)' }}>
              <div style={{ fontSize: 38, marginBottom: 14 }}>🔗</div>
              <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14 }}>No referrals yet</div>
              <div style={{ fontSize: 13 }}>Copy your referral link above and share it to start earning</div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="ref-table-wrap" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Referee', 'Joined', 'Status', 'Plan', 'Converted', 'Commission'].map((h, i) => (
                        <th key={h} style={{
                          textAlign: i === 5 ? 'right' : 'left',
                          padding: '10px 14px',
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.09em',
                          textTransform: 'uppercase', color: 'rgba(232,228,220,0.3)',
                          borderBottom: '1px solid rgba(255,255,255,0.06)', fontFamily: 'monospace',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.slice((refPage - 1) * REF_PAGE_SIZE, refPage * REF_PAGE_SIZE).map(r => (
                      <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '13px 14px' }}>
                          <div style={{ fontWeight: 600, color: '#E8E4DC', fontSize: 13 }}>{r.refereeName}</div>
                          <div style={{ fontSize: 11, color: 'rgba(232,228,220,0.3)', marginTop: 2 }}>{r.refereeEmail}</div>
                        </td>
                        <td style={{ padding: '13px 14px', color: 'rgba(232,228,220,0.55)', whiteSpace: 'nowrap' }}>{fmtDate(r.joinedAt)}</td>
                        <td style={{ padding: '13px 14px' }}><Pill status={r.status} /></td>
                        <td style={{ padding: '13px 14px', color: r.planName ? '#E8E4DC' : 'rgba(232,228,220,0.25)' }}>
                          {r.planName || '\u2014'}
                          {r.planAmount ? <span style={{ fontSize: 11, color: 'rgba(232,228,220,0.35)', marginLeft: 5 }}>{fmt$(r.planAmount)}</span> : null}
                        </td>
                        <td style={{ padding: '13px 14px', color: 'rgba(232,228,220,0.55)', whiteSpace: 'nowrap' }}>{fmtDate(r.convertedAt)}</td>
                        <td style={{ padding: '13px 14px', textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', color: r.commissionEarned > 0 ? '#D4A843' : 'rgba(232,228,220,0.25)' }}>
                          {r.commissionEarned > 0 ? fmt$(r.commissionEarned) : '\u2014'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="ref-cards-wrap" style={{ padding: '12px' }}>
                {referrals.slice((refPage - 1) * REF_PAGE_SIZE, refPage * REF_PAGE_SIZE).map(r => <ReferralCard key={r.id} r={r} />)}
              </div>
              {/* Pagination */}
              {referrals.length > REF_PAGE_SIZE && (() => {
                const totalPages = Math.ceil(referrals.length / REF_PAGE_SIZE);
                const start = (refPage - 1) * REF_PAGE_SIZE + 1;
                const end   = Math.min(refPage * REF_PAGE_SIZE, referrals.length);
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 14px',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    gap: 12, flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 11, color: 'rgba(232,228,220,0.35)', fontFamily: 'monospace' }}>
                      {start}–{end} of {referrals.length} referrals
                    </span>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button
                        onClick={() => setRefPage(p => Math.max(1, p - 1))}
                        disabled={refPage === 1}
                        style={{
                          padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                          background: refPage === 1 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          color: refPage === 1 ? 'rgba(232,228,220,0.2)' : 'rgba(232,228,220,0.7)',
                          cursor: refPage === 1 ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >← Prev</button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - refPage) <= 1)
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, i) => p === '…' ? (
                          <span key={`ellipsis-${i}`} style={{ fontSize: 12, color: 'rgba(232,228,220,0.25)', padding: '0 2px' }}>…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setRefPage(p)}
                            style={{
                              width: 30, height: 30, borderRadius: 7, fontSize: 12, fontWeight: 700,
                              background: p === refPage ? 'rgba(212,168,67,0.15)' : 'rgba(255,255,255,0.04)',
                              border: p === refPage ? '1px solid rgba(212,168,67,0.4)' : '1px solid rgba(255,255,255,0.07)',
                              color: p === refPage ? '#D4A843' : 'rgba(232,228,220,0.5)',
                              cursor: 'pointer', fontFamily: 'monospace',
                            }}
                          >{p}</button>
                        ))
                      }
                      <button
                        onClick={() => setRefPage(p => Math.min(totalPages, p + 1))}
                        disabled={refPage === totalPages}
                        style={{
                          padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                          background: refPage === totalPages ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          color: refPage === totalPages ? 'rgba(232,228,220,0.2)' : 'rgba(232,228,220,0.7)',
                          cursor: refPage === totalPages ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >Next →</button>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ── Withdrawals ── */}
      {tab === 'withdrawals' && (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, overflow: 'hidden' }}>
          {withdrawals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '52px 24px', color: 'rgba(232,228,220,0.3)' }}>
              <div style={{ fontSize: 38, marginBottom: 14 }}>📤</div>
              <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14 }}>No withdrawals yet</div>
              <div style={{ fontSize: 13 }}>Your payout history will appear here</div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="ref-table-wrap" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Date', 'Amount', 'Method', 'Status', 'Processed', 'Note'].map(h => (
                        <th key={h} style={{
                          textAlign: 'left', padding: '10px 14px',
                          fontSize: 10, fontWeight: 700, letterSpacing: '0.09em',
                          textTransform: 'uppercase', color: 'rgba(232,228,220,0.3)',
                          borderBottom: '1px solid rgba(255,255,255,0.06)', fontFamily: 'monospace',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map(w => (
                      <tr key={w.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '13px 14px', color: 'rgba(232,228,220,0.55)', whiteSpace: 'nowrap' }}>{fmtDate(w.requestedAt)}</td>
                        <td style={{ padding: '13px 14px', fontWeight: 700, color: '#D4A843', fontFamily: 'monospace' }}>{fmt$(w.amount)}</td>
                        <td style={{ padding: '13px 14px', color: 'rgba(232,228,220,0.6)' }}>{w.method || '—'}</td>
                        <td style={{ padding: '13px 14px' }}><Pill status={w.status} /></td>
                        <td style={{ padding: '13px 14px', color: 'rgba(232,228,220,0.55)', whiteSpace: 'nowrap' }}>{fmtDate(w.processedAt)}</td>
                        <td style={{ padding: '13px 14px', color: 'rgba(232,228,220,0.35)', fontSize: 12 }}>{w.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="ref-cards-wrap" style={{ padding: '12px' }}>
                {withdrawals.map(w => <WithdrawalCard key={w.id} w={w} />)}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── How it works ── */}
      <div style={{ marginTop: 36 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#E8E4DC', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>HOW IT WORKS</div>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>
        <div className="ref-how-grid">
          {[
            { step: '1', icon: '🔗', title: 'Share Your Link',  desc: 'Copy your unique referral link and share with friends, traders, or on social media.' },
            { step: '2', icon: '✍️', title: 'Friend Signs Up',  desc: 'Your friend registers using your link. They are automatically linked to you.' },
            { step: '3', icon: '💳', title: 'They Go Premium',  desc: `When they subscribe to a paid plan, you instantly earn ${(commissionRate * 100).toFixed(0)}% of their payment.` },
            { step: '4', icon: '💸', title: 'Withdraw',         desc: `Once your balance reaches $${withdrawalMin}, request a payout via your preferred method.` },
          ].map(item => (
            <div key={item.step} style={{
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 12, padding: '16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9, flexWrap: 'wrap' }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.28)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 800, color: '#D4A843', fontFamily: 'monospace',
                }}>{item.step}</div>
                <span style={{ fontSize: 13 }}>{item.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#E8E4DC', lineHeight: 1.3 }}>{item.title}</span>
              </div>
              <p style={{ margin: 0, fontSize: 12, color: 'rgba(232,228,220,0.4)', lineHeight: 1.65 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Withdrawal modal ── */}
      {showWithdraw && (
        <WithdrawModal
          stats={stats}
          withdrawalMin={withdrawalMin}
          onClose={() => setShowWithdraw(false)}
          onSuccess={load}
        />
      )}
    </div>
  );
}